import { splitPhoneNumberIntoParts } from '@application/utils';
import type { MtBreederOnboardingGetProfileQuery } from '@gql/graphql';
import { CountryCode } from '@gql/graphql';
import type { MtBreederOnboardingFormValues, MtProfileBreederValues } from '@presentation/pages';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';

export const useProfileInitialValuesBuilders = (
  translations: string[],
  profile?: MtBreederOnboardingGetProfileQuery['accountMe'] | null,
) => {
  const getTranslatedField = useCallback(
    (
      lng: string,
      field: keyof NonNullable<
        NonNullable<MtBreederOnboardingGetProfileQuery['accountMe']>['dogBreeder']
      >['translations'][number],
    ): string | undefined => {
      const allTranslations = profile?.dogBreeder?.translations;

      const valueInCurrentLanguage = allTranslations?.find((x) => x.language === lng)?.[field];
      if (valueInCurrentLanguage) return valueInCurrentLanguage as string;
    },
    [profile?.dogBreeder],
  );

  const profileInitialValues = useMemo(():
    | MtBreederOnboardingFormValues
    | MtProfileBreederValues => {
    const images = (profile?.dogBreeder?.imgs ?? [])
      .filter((img) => !!img.small)
      .map((img) => img.small) as string[];

    const dogBreedsIds = (profile?.dogBreeder?.breederBreeds ?? []).map(
      (breed) => breed.dogBreedId,
    );

    let dogsPedigree = profile?.dogBreeder?.dogsPedigree;
    if (!dogBreedsIds.length) {
      dogsPedigree = undefined;
    } else if (profile?.dogBreeder?.dogsPedigree === null) {
      dogsPedigree = null;
    }
    let phonePrefix;
    let phoneWithoutprefix;
    if (profile?.dogBreeder?.displayPhone) {
      const { phoneNumberWithoutPrefix, phonePrefixCountry } = splitPhoneNumberIntoParts(
        profile.dogBreeder.displayPhone,
      );
      phonePrefix = phonePrefixCountry;
      phoneWithoutprefix = phoneNumberWithoutPrefix;
    }
    return {
      profileImage: profile?.imgs?.[0]?.small ?? undefined,
      images: images.length ? images : [undefined],

      // Step 1

      spokenLanguages: profile?.dogBreeder?.spokenLanguages ?? undefined,

      city: profile?.address?.city ?? undefined,
      region: profile?.address?.region ?? undefined,
      zip: profile?.address?.zip ?? undefined,
      country: profile?.address?.country ?? CountryCode.BE,

      // Step 2
      level: profile?.dogBreeder?.level,
      agreementNumber: profile?.dogBreeder?.agreementNumber,

      // Step 3
      breedingName: profile?.dogBreeder?.breedingName ?? undefined,
      dogBreedsIds,
      websiteUrl: profile?.dogBreeder?.websiteUrl ?? undefined,
      facebookUrl: profile?.dogBreeder?.facebookUrl ?? undefined,

      // Step 4
      dogsPedigree,
      littersByFemale: profile?.dogBreeder?.littersByFemale ?? undefined,
      femaleBreedersCount: profile?.dogBreeder?.femaleBreedersCount ?? undefined,
      motherRestBetweenLitters: profile?.dogBreeder?.motherRestBetweenLitters ?? undefined,
      dogsConsanguinity: profile?.dogBreeder?.dogsConsanguinity ?? undefined,
      medicalTestsPerformed: profile?.dogBreeder?.medicalTestsPerformed ?? undefined,
      veterinaryInfo: profile?.dogBreeder?.veterinaryInfo ?? undefined,
      hkFile: profile?.dogBreeder?.hkFile ?? undefined,
      certificationFile: profile?.dogBreeder?.certificationFile ?? undefined,
      permissionFiles: profile?.dogBreeder?.permissionFiles ?? undefined,
      identityFiles: profile?.dogBreeder?.identityFiles ?? undefined,
      breedClubFiles: profile?.dogBreeder?.breedClubFiles ?? undefined,
      translated: {
        fr: {
          id: getTranslatedField('fr', 'id') as number | undefined,
          description: getTranslatedField('fr', 'description'),
          dogsLivingPlace: getTranslatedField('fr', 'dogsLivingPlace'),
          breederDogsSelection: getTranslatedField('fr', 'breederDogsSelection'),
          puppiesTraining: getTranslatedField('fr', 'puppiesTraining'),
          puppiesLivingPlace: getTranslatedField('fr', 'puppiesLivingPlace'),
        },
        nl: {
          id: getTranslatedField('nl', 'id') as number | undefined,
          description: getTranslatedField('nl', 'description'),
          dogsLivingPlace: getTranslatedField('nl', 'dogsLivingPlace'),
          breederDogsSelection: getTranslatedField('nl', 'breederDogsSelection'),
          puppiesTraining: getTranslatedField('nl', 'puppiesTraining'),
          puppiesLivingPlace: getTranslatedField('nl', 'puppiesLivingPlace'),
        },
        de: {
          id: getTranslatedField('de', 'id') as number | undefined,
          description: getTranslatedField('de', 'description'),
          dogsLivingPlace: getTranslatedField('de', 'dogsLivingPlace'),
          breederDogsSelection: getTranslatedField('de', 'breederDogsSelection'),
          puppiesTraining: getTranslatedField('de', 'puppiesTraining'),
          puppiesLivingPlace: getTranslatedField('de', 'puppiesLivingPlace'),
        },
      },
      // Mt profile
      firstName: profile?.firstName ?? undefined,
      lastName: profile?.lastName ?? undefined,
      email: profile?.dogBreeder?.displayEmail ?? undefined,
      phonePrefixCountry: phonePrefix,
      phone: phoneWithoutprefix,
      birthday: profile?.birthday ? dayjs(profile.birthday) : undefined,
      gender: profile?.gender ?? undefined,
      displayName: profile?.dogBreeder?.displayName ?? undefined,
      translations,
      language: profile?.language ?? undefined,
      veterinaryNumber: profile?.dogBreeder?.veterinaryInfo ?? undefined,
    };
  }, [getTranslatedField, profile, translations]);

  return { profileInitialValues };
};
